import { useQuery } from '@tanstack/react-query'
import { useState, useCallback } from 'react'
import { axiosClient } from '@/utils/axios'

interface CaptchaConfig {
  enabled: boolean
  siteKey?: string
  provider?: 'turnstile' | 'recaptcha'
}

/**
 * Hook to manage CAPTCHA state and verification
 *
 * Usage:
 * ```tsx
 * const { config, token, onVerify, reset, isEnabled, isReady } = useCaptcha()
 *
 * // In your form submit:
 * if (isEnabled && !token) return // Block submission
 *
 * // Pass token to API:
 * register({ ...formData, captchaToken: token })
 * ```
 */
export function useCaptcha() {
  const [token, setToken] = useState<string | null>(null)

  // Fetch CAPTCHA configuration from backend
  const {
    data: config,
    isLoading,
    error,
  } = useQuery<CaptchaConfig>({
    queryKey: ['captcha-config'],
    queryFn: async () => {
      const response = await axiosClient<{ data: CaptchaConfig }>({
        method: 'GET',
        url: '/api/v5/auth/captcha/config',
      })
      // Handle both response formats
      const typedResponse = response as { data?: CaptchaConfig } | CaptchaConfig
      return 'data' in typedResponse && typedResponse.data
        ? typedResponse.data
        : (typedResponse as CaptchaConfig)
    },
    staleTime: Infinity, // Config rarely changes
    retry: 1,
  })

  /**
   * Callback when CAPTCHA is verified
   */
  const onVerify = useCallback((newToken: string) => {
    setToken(newToken)
  }, [])

  /**
   * Callback when CAPTCHA expires
   */
  const onExpire = useCallback(() => {
    setToken(null)
  }, [])

  /**
   * Callback when CAPTCHA verification fails
   */
  const onError = useCallback(() => {
    setToken(null)
  }, [])

  /**
   * Reset CAPTCHA state (call after form submission error)
   */
  const reset = useCallback(() => {
    setToken(null)
  }, [])

  return {
    /** CAPTCHA configuration from backend */
    config,
    /** Whether config is still loading */
    isLoading,
    /** Error loading config */
    error,
    /** Current CAPTCHA token (null if not verified) */
    token,
    /** Callback for when CAPTCHA is verified */
    onVerify,
    /** Callback for when CAPTCHA expires */
    onExpire,
    /** Callback for when CAPTCHA errors */
    onError,
    /** Reset CAPTCHA state */
    reset,
    /** Whether CAPTCHA is enabled on the backend */
    isEnabled: config?.enabled ?? false,
    /** Whether form is ready to submit (CAPTCHA not enabled OR token obtained) */
    isReady: !isLoading && (!config?.enabled || !!token),
  }
}

export type { CaptchaConfig }
